/**
 * OperatorSelector Component
 *
 * Provides a dropdown for selecting filter operators (equals, contains, etc.).
 * Filters available operators based on the selected field's data type.
 * Labels are context-aware: dates show "is after", numbers show ">", etc.
 *
 * @module FilterEditor/OperatorSelector
 */

import React, { useMemo } from 'react';
import type { LinkedTableFilterApi, TableFieldApiResponse, FieldDataType } from '@loader/shared-types';
import { FILTER_OPERATORS_BY_TYPE } from '@loader/shared-types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@loader/ui';

import { getOperatorLabel } from './types';

interface OperatorSelectorProps {
  /** Whether the user can edit (false for viewers) */
  canEdit?: boolean;
  filter: LinkedTableFilterApi;
  selectedField: TableFieldApiResponse | undefined;
  onOperatorChange: (operator: string) => void;
}

export const OperatorSelector: React.FC<OperatorSelectorProps> = ({
  canEdit = true,
  filter,
  selectedField,
  onOperatorChange,
}) => {
  const fieldType = selectedField?.data_type || 'text';

  /**
   * Get filtered operators with type-aware labels
   */
  const operators = useMemo(() => {
    const allowedOperators = FILTER_OPERATORS_BY_TYPE[fieldType as FieldDataType] || FILTER_OPERATORS_BY_TYPE.text;
    return allowedOperators.map((op) => ({
      value: op,
      label: getOperatorLabel(op, fieldType),
    }));
  }, [fieldType]);

  // Get the label for the currently selected operator
  const selectedLabel = getOperatorLabel(filter.operator, fieldType);

  return (
    <Select value={filter.operator} onValueChange={onOperatorChange} disabled={!canEdit}>
      <SelectTrigger className="bg-background w-full">
        <SelectValue placeholder="Select operator">{selectedLabel}</SelectValue>
      </SelectTrigger>
      <SelectContent className="bg-background">
        {operators.map((op) => (
          <SelectItem key={op.value} value={op.value}>
            {op.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
